import { useState } from 'react';
import { useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import { Button, Pagination, User } from 'components/common';
import {
  Box,
  BoxEmpty,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import {
  Modal,
  ModalContent,
  ModalFooter,
  ModalPortal
} from 'components/common/modal';

import type { User as IUser } from 'shared-types';
import type { PageState } from 'types';

type UserWithPaths = IUser & { paths: { title: string }[] };

interface Props {
  paths: PageState['path'][];
  url: string;
}

const PathsInProductUsers = ({ paths, url }: Props) => {
  const { messages } = useReduxState();

  const [page, setPage] = useState(1);
  const [modalVisible, setModalVisible] = useState(false);

  const { data, loading } = useApiData({
    url: `${url}-users?page=${page}`,
    onlyIf: modalVisible
  });

  const hideModal = () => setModalVisible(false);

  const showModal = () => {
    setModalVisible(true);
    setPage(1);
  };

  return (
    <>
      <Button
        category="default"
        disabled={!paths || !paths.length}
        onClick={showModal}
        size="header"
        style={{ marginLeft: 10 }}
        text={messages.commonViewUsers}
      />
      <ModalPortal>
        <Modal
          closeButton={true}
          onHide={hideModal}
          title={messages.settingsPathsUsers}
          visible={modalVisible}
          width="wide"
        >
          <ModalContent style={{ padding: 0 }}>
            <Box loading={loading} padding={0}>
              {data?.users?.length ? (
                <BoxTable>
                  <BoxTableHeader>
                    <BoxTableCell>{messages.commonUser}</BoxTableCell>
                    <BoxTableCell>{messages.paths}</BoxTableCell>
                  </BoxTableHeader>

                  {data.users.map((user: UserWithPaths) => (
                    <BoxTableRow key={user.id}>
                      <BoxTableCell>
                        <User enableProfileLink={true} user={user} />
                      </BoxTableCell>

                      <BoxTableCell>
                        {user.paths.map((path) => path.title).join(', ')}
                      </BoxTableCell>
                    </BoxTableRow>
                  ))}
                </BoxTable>
              ) : (
                <BoxEmpty text={messages.commonNoUsers} />
              )}
            </Box>
          </ModalContent>

          {data?.users?.length ? (
            <ModalFooter>
              <Pagination
                currentPage={page}
                numberOfPages={data?.pages}
                onPageChange={({ page }) => setPage(page)}
                style={{ marginTop: 0 }}
              />
            </ModalFooter>
          ) : null}
        </Modal>
      </ModalPortal>
    </>
  );
};

export default PathsInProductUsers;
